<template>
  <div
    :class="[
      'flex',
      {
        'flex-col items-stretch': !_global.isPcMode,
        'items-center': _global.isPcMode,
      },
    ]"
  >
    <div
      :class="[
        'flex-1 mr-2 my-2 flex items-start',
        {
          'gap-4': _global.isPcMode,
          'gap-2 flex-col': !_global.isPcMode,
        },
      ]"
    >
      <div
        :class="[
          'relative',
          {
            'basis-2/5': _global.isPcMode,
            'w-full': !_global.isPcMode,
          },
        ]"
      >
        <HanziEditor
          v-model="hanziContent"
          class="g-underline-input w-100%"
          placeholder="汉字"
          @update:model-value="onContentUpdate"
          @focus="onFocus"
          @blur="onBlur"
        />

        <div
          v-if="hanziInvalidMessage && showInvalidTip"
          class="text-red text-14px"
        >
          {{ hanziInvalidMessage }}
        </div>
      </div>

      <div
        :class="{
          'basis-3/5': _global.isPcMode,
          'w-full': !_global.isPcMode,
        }"
      >
        <HanziEditor
          v-model="analysisContent"
          class="g-underline-input w-100%"
          placeholder="解析"
          @focus="onFocus"
          @blur="onBlur"
          @update:model-value="onContentUpdate"
        />
        <div
          v-if="definitionInvalidMessage && showInvalidTip"
          class="text-red text-14px"
        >
          {{ definitionInvalidMessage }}
        </div>
      </div>
    </div>

    <template v-if="_global.isPcMode">
      <div
        v-if="!card.illustration || illustrationUploading"
        class="h-78px w-78px flex items-center justify-center border border-dashed rounded bg-[var(--surface-100)] cursor-pointer"
        @click="emit('add-illustration')"
      >
        <ProgressSpinner
          v-if="illustrationUploading"
          class="w-36px h-36px p-11px"
        />

        <Icon
          v-else
          name="illustration"
          class="w-23px text-gray-600"
        />
      </div>
    </template>
    <template v-if="_global.isMobileMode">
      <div
        v-if="!card.illustration || illustrationUploading"
        class="ml-auto mt-2"
        @click="emit('add-illustration')"
      >
        <ProgressSpinner
          v-if="illustrationUploading"
          class="w-23px h-23px p-11px"
        />

        <Icon
          v-else
          name="illustration"
          class="w-23px text-gray-600"
        />
      </div>
    </template>

    <Image
      v-if="card.illustration && !illustrationUploading"
      preview
      :class="[
        'illustration w-fit',
        {
          'mt-2 ml-auto': !_global.isPcMode,
        },
      ]"
    >
      <template #image>
        <div class="relative">
          <Img
            :assetId="card.illustration"
            class="h-78px w-78px rounded-8px object-cover"
            @click.stop
          />

          <Icon
            name="image-delete"
            class="absolute top-0 right-0 z-1 cursor-pointer w-18px"
            @click="emit('remove-illustration')"
          />
        </div>
      </template>
      <template #preview="slotProps">
        <img
          :src="_global.assetUrl(card.illustration)"
          class="object-cover"
          :style="slotProps.style"
          @click="slotProps.onClick"
        />
      </template>
    </Image>
  </div>
</template>
<script setup lang="ts">
import type { CardResponse } from '@/api/package-source'
import { computed, ref } from 'vue'
import Image from 'primevue/image'
import ProgressSpinner from 'primevue/progressspinner'
import HanziEditor from './HanziEditor.vue'
import type { KidHanziCard, StringOrHanzi } from '@/types/model'

const props = defineProps<{
  cardRes: CardResponse
  card: KidHanziCard
  illustrationUploading: boolean
}>()

const hanziContent = ref<StringOrHanzi[]>(props.card.hanzi)
const analysisContent = ref<StringOrHanzi[]>(props.card.analysis)

const emit = defineEmits<{
  focus: []
  update: [KidHanziCard]
  'add-illustration': []
  'remove-illustration': []
}>()

const showInvalidTip = ref(false)

function calcLength(content: StringOrHanzi[]): number {
  let result = 0
  for (const item of content) {
    if (typeof item === 'string') {
      result += item.length
    } else {
      result += 1
    }
  }
  return result
}

const hanziInvalidMessage = computed(() => {
  if (
    hanziContent.value.length !== 1 ||
    hanziContent.value[0]?.[0] !== 'hanzi'
  ) {
    return _t('cardview.validate_kid_hanzi_1')
  }

  if (calcLength(hanziContent.value) > 10) {
    return _t('cardview.validate_kid_hanzi_3')
  }
  return ''
})

const definitionInvalidMessage = computed(() => {
  if (analysisContent.value.length === 0) {
    return _t('cardview.validate_kid_hanzi_2')
  }

  if (calcLength(analysisContent.value) > 50) {
    return _t('cardview.validate_kid_hanzi_4')
  }

  return ''
})

// 事件触发时，hanziContent and analysisContent both have the new value
function onContentUpdate() {
  emit('update', {
    ...props.card,
    hanzi: hanziContent.value,
    analysis: analysisContent.value,
  } as KidHanziCard)
}

function onFocus() {
  emit('focus')
  showInvalidTip.value = false
}

function onBlur() {
  showInvalidTip.value = true
}
</script>
<style scoped>
.enword-list {
  width: 100%;
  top: 100%;
  box-shadow: 0px 4px 4px 0px var(--slate-300);
  border: 1px solid var(--surface-300);
  background-color: white;
  border-radius: 0px 0px 8px 8px;
  z-index: 2;
}
</style>
